import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Lock, LogOut, Download, Search, RefreshCw, BarChart2, Users, Building2, Layers, Globe, Phone } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('sl_admin_token') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Automotive state
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);

  // Voice state
  const [voiceSubmissions, setVoiceSubmissions] = useState([]);
  const [voiceTotal, setVoiceTotal] = useState(0);

  const [activeTab, setActiveTab] = useState('auto'); // 'auto' | 'voice'
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (searchTerm = '') => {
    if (!token) return;
    setLoading(true);
    try {
      const [subRes, statsRes, voiceRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/submissions?limit=200&search=${encodeURIComponent(searchTerm)}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND_URL}/api/admin/voice-submissions?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if ([subRes, statsRes, voiceRes].some(r => r.status === 401)) {
        setToken('');
        sessionStorage.removeItem('sl_admin_token');
        toast.error('Session expired. Please log in again.');
        return;
      }
      const subData = await subRes.json();
      const statsData = await statsRes.json();
      const voiceData = await voiceRes.json();
      setSubmissions(subData.submissions || []);
      setTotal(subData.total || 0);
      setStats(statsData);
      setVoiceSubmissions(voiceData.submissions || []);
      setVoiceTotal(voiceData.total || 0);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) fetchData(search); }, [token, fetchData, search]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        const data = await res.json();
        setToken(data.token);
        sessionStorage.setItem('sl_admin_token', data.token);
        toast.success('Welcome back!');
      } else {
        toast.error('Invalid credentials');
      }
    } catch { toast.error('Network error'); }
    finally { setLoginLoading(false); }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'salibalabs_automotive.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded!');
    } catch { toast.error('Export error'); }
  };

  const handleExportExcel = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/export-excel`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'salibalabs_all_leads.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded! Both tabs included.');
    } catch { toast.error('Export error'); }
  };

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); };
  const handleLogout = () => { setToken(''); sessionStorage.removeItem('sl_admin_token'); };

  // ── LOGIN SCREEN ──────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0a' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8" style={{ backdropFilter: 'blur(16px)' }}>
            <div className="flex justify-center mb-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.12)' }}>
                <Lock size={24} style={{ color: '#C9A84C' }} />
              </div>
            </div>
            <h1 className="text-xl font-semibold text-white text-center mb-1" style={{ fontFamily: 'Space Grotesk' }}>SalibaLabs Admin</h1>
            <p className="text-xs text-white/50 text-center mb-6">Waitlist submissions dashboard</p>

            <form onSubmit={handleLogin}>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1.5">Username</label>
                  <input
                    type="email"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    className="w-full px-4 py-3 rounded-xl border border-white/15 bg-black/50 text-white text-sm placeholder:text-white/30 input-gold outline-none"
                    data-testid="admin-username-input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1.5">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 rounded-xl border border-white/15 bg-black/50 text-white text-sm placeholder:text-white/30 input-gold outline-none"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
              <button type="submit" disabled={loginLoading || !username || !password}
                className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200"
                style={{ background: (loginLoading || !username || !password) ? 'rgba(201,168,76,0.4)' : '#C9A84C', color: '#0a0a0a', cursor: (loginLoading || !username || !password) ? 'not-allowed' : 'pointer' }}
                data-testid="admin-login-button">
                {loginLoading ? 'Signing in...' : 'Access Dashboard'}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── DASHBOARD ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      {/* Header */}
      <div className="border-b border-white/8 bg-black/60 sticky top-0 z-40" style={{ backdropFilter: 'blur(12px)' }}>
        <div className="mx-auto w-[min(1120px,calc(100%-32px))] h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              <span className="text-white">Saliba</span><span style={{ color: '#C9A84C' }}>Labs</span><span className="text-white/40">.ai</span>
            </span>
            <span className="px-2 py-0.5 rounded text-xs font-mono-accent border border-[#C9A84C]/30 text-[#C9A84C]">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchData(search)} className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white transition-colors" title="Refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleExport} className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border border-white/10 text-white/70 hover:text-white hover:border-[#C9A84C]/40 transition-all">
              <Download size={14} /> CSV
            </button>
            <button onClick={handleExportExcel}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border border-[#C9A84C]/30 hover:border-[#C9A84C]/60 transition-all"
              style={{ color: '#C9A84C' }}>
              <Download size={14} /> Excel
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-white/50 hover:text-white transition-colors">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto w-[min(1120px,calc(100%-32px))] py-8">
        {/* Stats row */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { icon: <Users size={18} />, value: total, label: 'Automotive Signups' },
              { icon: <Globe size={18} />, value: voiceTotal, label: 'Voice Leads' },
              { icon: <Layers size={18} />, value: stats.totalRooftops || 0, label: 'Total Rooftops' },
              { icon: <Building2 size={18} />, value: stats.titleBreakdown?.[0]?.title || '—', label: 'Top Title' },
            ].map((s, i) => (
              <div key={i} className="rounded-xl border border-white/8 bg-white/[0.025] p-4">
                <div className="flex items-center gap-2 mb-1" style={{ color: '#C9A84C' }}>{s.icon}
                  <span className="text-xs text-white/50 font-medium">{s.label}</span>
                </div>
                <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 p-1 rounded-xl border border-white/8 bg-white/[0.02] w-fit">
          {[{ id: 'auto', label: `Automotive (${total})`, icon: <Building2 size={14} /> }, { id: 'voice', label: `Voice & Other (${voiceTotal})`, icon: <Globe size={14} /> }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
              style={{
                background: activeTab === tab.id ? '#C9A84C' : 'transparent',
                color: activeTab === tab.id ? '#0a0a0a' : 'rgba(255,255,255,0.5)',
              }}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {/* Search (automotive only) */}
        {activeTab === 'auto' && (
          <form onSubmit={handleSearch} className="flex gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                placeholder="Search by dealership, name..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-white/10 bg-black/50 text-white text-sm placeholder:text-white/30 input-gold outline-none" />
            </div>
            <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200" style={{ background: '#C9A84C', color: '#0a0a0a' }}>Search</button>
            {search && (
              <button type="button" onClick={() => { setSearch(''); setSearchInput(''); }}
                className="px-4 py-2.5 rounded-xl text-sm border border-white/10 text-white/60 hover:text-white transition-colors">Clear</button>
            )}
          </form>
        )}

        {/* AUTOMOTIVE TABLE */}
        {activeTab === 'auto' && (
          <div className="rounded-2xl border border-white/8 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-submissions-table">
                <thead>
                  <tr className="border-b border-white/8" style={{ background: '#121213' }}>
                    {['Submitted', 'Name', 'Email', 'Title', 'Dealership', 'Rooftops', 'DMS', 'IMS', 'Cell', 'AI Capabilities'].map(h => (
                      <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={9} className="text-center py-12 text-white/40 text-sm"><RefreshCw size={20} className="animate-spin mx-auto mb-2" />Loading...</td></tr>}
                  {!loading && submissions.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-white/40 text-sm">{search ? 'No results.' : 'No automotive submissions yet.'}</td></tr>}
                  {!loading && submissions.map((s, i) => (
                    <tr key={s._id || i} className="border-b border-white/5 transition-colors"
                      style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent'}>
                      <td className="px-4 py-3.5 text-xs text-white/50 font-mono-accent whitespace-nowrap">{formatDate(s.createdAt)}</td>
                      <td className="px-4 py-3.5 text-sm text-white font-medium whitespace-nowrap">{s.firstName} {s.lastName}</td>
                      <td className="px-4 py-3.5 text-xs text-white/70">{s.email || '—'}</td>
                      <td className="px-4 py-3.5">
                        <span className="px-2 py-0.5 rounded text-xs border border-[#C9A84C]/25" style={{ color: '#C9A84C', background: 'rgba(201,168,76,0.08)' }}>{s.title}</span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-white/80">{s.dealershipName}</td>
                      <td className="px-4 py-3.5 text-sm text-white/70 font-mono-accent text-center">{s.rooftops}</td>
                      <td className="px-4 py-3.5 text-xs text-white/60">{s.dms || '—'}</td>
                      <td className="px-4 py-3.5 text-xs text-white/60">{s.ims || '—'}</td>
                      <td className="px-4 py-3.5 text-xs text-white/60 font-mono-accent whitespace-nowrap">{s.cellPhone || '—'}</td>
                      <td className="px-4 py-3.5 text-xs text-white/55 max-w-[180px]">
                        {s.aiCapabilities?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {s.aiCapabilities.map((c, j) => (
                              <span key={j} className="px-1.5 py-0.5 rounded text-[10px] border border-white/10 text-white/50">{c.replace(/_/g,' ')}</span>
                            ))}
                          </div>
                        ) : <span className="text-white/25">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && submissions.length > 0 && (
              <div className="px-4 py-3 border-t border-white/8 text-xs text-white/40" style={{ background: '#0e0e0f' }}>
                Showing {submissions.length} of {total}{search && ` — filtered by “${search}”`}
              </div>
            )}
          </div>
        )}

        {/* VOICE TABLE */}
        {activeTab === 'voice' && (
          <div className="rounded-2xl border border-white/8 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/8" style={{ background: '#121213' }}>
                    {['Submitted', 'Name', 'Email', 'Cell', 'Business URL', 'Use Cases', 'AI Capabilities'].map(h => (
                      <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-white/50 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="text-center py-12 text-white/40 text-sm"><RefreshCw size={20} className="animate-spin mx-auto mb-2" />Loading...</td></tr>}
                  {!loading && voiceSubmissions.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-white/40 text-sm">No voice submissions yet.</td></tr>}
                  {!loading && voiceSubmissions.map((s, i) => (
                    <tr key={s._id || i} className="border-b border-white/5 transition-colors"
                      style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent'}>
                      <td className="px-4 py-3.5 text-xs text-white/50 font-mono-accent whitespace-nowrap">{formatDate(s.createdAt)}</td>
                      <td className="px-4 py-3.5 text-sm text-white font-medium whitespace-nowrap">{s.firstName} {s.lastName}</td>
                      <td className="px-4 py-3.5 text-xs text-white/70">{s.email || '—'}</td>
                      <td className="px-4 py-3.5 text-xs text-white/60 font-mono-accent whitespace-nowrap">{s.cellPhone || '—'}</td>
                      <td className="px-4 py-3.5 text-xs text-white/60">{s.businessUrl || '—'}</td>
                      <td className="px-4 py-3.5 text-xs text-white/55 max-w-[160px]">
                        {s.useCases?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {s.useCases.map((c, j) => (
                              <span key={j} className="px-1.5 py-0.5 rounded text-[10px] border border-[#C9A84C]/20" style={{ color: '#C9A84C' }}>{c.replace(/_/g,' ')}</span>
                            ))}
                          </div>
                        ) : <span className="text-white/25">—</span>}
                      </td>
                      <td className="px-4 py-3.5 text-xs text-white/55 max-w-[160px]">
                        {s.aiCapabilities?.length ? (
                          <div className="flex flex-wrap gap-1">
                            {s.aiCapabilities.map((c, j) => (
                              <span key={j} className="px-1.5 py-0.5 rounded text-[10px] border border-white/10 text-white/50">{c.replace(/_/g,' ')}</span>
                            ))}
                          </div>
                        ) : <span className="text-white/25">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!loading && voiceSubmissions.length > 0 && (
              <div className="px-4 py-3 border-t border-white/8 text-xs text-white/40" style={{ background: '#0e0e0f' }}>
                Showing {voiceSubmissions.length} of {voiceTotal} voice leads
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
